import { Clock, Share, CaretDown, CaretUp } from "@phosphor-icons/react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

/**
 * The always-visible row on a match card:
 * time/table · players · score · status/round/bo + action buttons
 */
export default function MatchCardMainRow({
  match, p1Name, p2Name,
  isCompleted, isLive, isScheduled, canRecord, expanded,
  onToggleExpand, onReschedule, onShare,
}) {
  const scoringType = match.scoring_type || match.tournament_scoring_type;
  return (
    <div
      className={`flex items-center gap-3 px-4 py-3 ${canRecord ? "cursor-pointer" : ""}`}
      onClick={() => canRecord && !isLive && onToggleExpand()}
      data-testid={`match-row-${match.id}`}
    >
      {/* Schedule info */}
      <div className="flex flex-col items-center w-20 flex-shrink-0">
        {match.scheduled_time ? (
          <span className="text-sm font-bold text-foreground">{match.scheduled_time}</span>
        ) : (
          <span className="text-xs text-muted-foreground">No time</span>
        )}
        {match.table_name && (
          <span className="text-[10px] text-muted-foreground mt-0.5">{match.table_name}</span>
        )}
      </div>

      <div className="w-px h-10 bg-border flex-shrink-0" />

      {/* Players & Score */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`text-sm font-medium flex-1 text-right truncate ${isCompleted && match.winner_id === match.player1_id ? "text-primary font-bold" : ""}`}>
            {p1Name}
          </span>
          <div className="flex items-center gap-1 px-2 flex-shrink-0">
            <span className={`font-heading text-base font-bold w-5 text-center ${isCompleted && match.winner_id === match.player1_id ? "text-primary" : ""}`}>
              {match.player1_score || 0}
            </span>
            <span className="text-muted-foreground text-xs">-</span>
            <span className={`font-heading text-base font-bold w-5 text-center ${isCompleted && match.winner_id === match.player2_id ? "text-primary" : ""}`}>
              {match.player2_score || 0}
            </span>
          </div>
          <span className={`text-sm font-medium flex-1 truncate ${isCompleted && match.winner_id === match.player2_id ? "text-primary font-bold" : ""}`}>
            {p2Name}
          </span>
        </div>
      </div>

      {/* Status & Meta */}
      <div className="flex items-center gap-2 flex-shrink-0">
        <Badge
          variant="outline"
          className={`text-[10px] capitalize ${
            isCompleted ? "border-primary/40 text-primary" :
            isLive ? "border-secondary/40 text-secondary" :
            "border-border text-muted-foreground"
          }`}
        >
          {match.status}
        </Badge>
        {match.round && (
          <Badge variant="outline" className="text-[10px] capitalize border-border text-muted-foreground">
            {match.round.replace(/_/g, " ")}
          </Badge>
        )}
        {match.best_of && (
          <span className="text-[10px] text-muted-foreground">Bo{match.best_of}</span>
        )}
        {scoringType && (
          <Badge
            variant="outline"
            className={`text-[9px] capitalize ${
              scoringType === "live"
                ? "border-destructive/40 text-destructive"
                : "border-primary/30 text-primary"
            }`}
            data-testid={`scoring-type-${match.id}`}
          >
            {scoringType === "live" ? "Live" : "Submission"}
          </Badge>
        )}
        {canRecord && (
          <div className="ml-1">
            {expanded
              ? <CaretUp className="w-4 h-4 text-muted-foreground" />
              : <CaretDown className="w-4 h-4 text-muted-foreground" />}
          </div>
        )}
        {isScheduled && onReschedule && (
          <Button
            size="sm" variant="ghost"
            className="h-6 px-2 text-[10px] text-primary hover:bg-primary/10"
            onClick={(e) => { e.stopPropagation(); onReschedule(match); }}
            data-testid={`reschedule-match-${match.id}`}
          >
            <Clock className="w-3 h-3 mr-1" />Reschedule
          </Button>
        )}
        {isCompleted && onShare && (
          <Button
            size="sm" variant="ghost"
            className="h-6 px-2 text-[10px] text-secondary hover:bg-secondary/10"
            onClick={(e) => { e.stopPropagation(); onShare(match); }}
            data-testid={`share-match-${match.id}`}
          >
            <Share className="w-3 h-3 mr-1" weight="fill" />Share
          </Button>
        )}
      </div>
    </div>
  );
}
